import { BoxIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { SettingsNavigation } from '@/components/organisms/Navbar/SettingsNavigation/SettingsNavigation';
import { PageSubHeader, PageSubHeaderBreadcrumbs } from '@/components/organisms/PageSubHeader';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { selectAccountSlug } from '@/redux/session/selectors';

import CreateDeskTile from './CreateDeskTile';

export default function Desks() {
  const slug = useSelector(selectAccountSlug);
  const { t } = useTranslation();
  useTitle(t('pages.environments', { 0: slug ?? '-' }));

  const { desks, listStatus } = useDesks();
  const deskId = useDeskId();
  const firstDeskId = listStatus === 'success' && desks[0]?.desk_id;

  if (listStatus === 'success' && desks.length) {
    return <Navigate to={`/${slug}/environments/${deskId ?? firstDeskId}`} replace />;
  }

  return (
    <Layout>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <div className="flex h-8 w-8 items-center justify-center">
                      <BoxIcon size={16} className="text-muted-foreground" />
                    </div>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <span className="text-sm leading-5 font-normal">
                      {t('common.environments')}
                    </span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
      />
      <div className="flex min-h-0 flex-1">
        <SettingsNavigation />
        <div className="flex min-h-0 flex-1 flex-col">
          <PageContentWrapper>
            <SettingsContent className="max-w-(--global-container-width)">
              <ContextualHelp title={t('environments.contextual.title')} name="desk" size="full">
                {t('environments.contextual.body')}
              </ContextualHelp>
              <CreateDeskTile />
            </SettingsContent>
          </PageContentWrapper>
        </div>
      </div>
    </Layout>
  );
}
